import React from 'react'

import styled from '@emotion/styled'
import usePromise from 'react-use-promise'

import Loading from './Loading'
import { REJECTED, RESOLVED } from './constants'

const LoadingStepsWrapper = styled.div`
	width: 30%;
	height: 90vh;
	display: flex;
	flex-direction: column;
	justify-content: center;
	margin: auto;
`

/**
 * Component to evaluate all the promises resolved
 */
const PreviewComponent = ({ templateQuery, pageContext, component, data }) => {
	const allReady =
		templateQuery[STATUS] === RESOLVED &&
		pageContext[STATUS] === RESOLVED &&
		component[STATUS] === RESOLVED &&
		data[STATUS] === RESOLVED

	const externalError =
		templateQuery[STATUS] === REJECTED ||
		pageContext[STATUS] === REJECTED ||
		component[STATUS] === REJECTED ||
		data[STATUS] === REJECTED

	if (allReady) {
		const Component = component[RESULT].default

		return <Component pageContext={pageContext[RESULT].context} data={data[RESULT].data} />
	} else {
		return (
			<LoadingStepsWrapper>
				<Loading
					message="Looking for Page Template"
					status={templateQuery[STATUS]}
					error={templateQuery[ERROR]}
					externalError={externalError}
				/>
				<Loading
					message="Procesing Page Context"
					status={pageContext[STATUS]}
					error={pageContext[ERROR]}
					externalError={externalError}
				/>
				<Loading
					message="Loading Template Component"
					status={component[STATUS]}
					error={component[ERROR]}
					externalError={externalError}
				/>
				<Loading
					message="Loading Data for Template"
					status={data[STATUS]}
					error={data[ERROR]}
					externalError={externalError}
				/>
			</LoadingStepsWrapper>
		)
	}
}

const RESULT = 0,
	ERROR = 1,
	STATUS = 2

/**
 * Component to inject graphql queries in a component with a loader
 * @param {object} param0
 * @param {string} param0.slug
 * @param {object} param0.queries
 * @param {object} param0.pageContextFactory
 * @param {object} param0.client Apollo Client Object
 * @param {object} param0.clients Apollo Client Objects by source name
 * @param {object} param0.TEMPLATE_QUERY Graphql Document object
 * @param {Promise} importFunction Promise wrapping the import() context
 */
export const Preview = ({ slug, queries, pageContextFactory, client, clients, TEMPLATE_QUERY, importFunction }) => {
	const templateQuery = usePromise(() => {
		return client
			.query({
				query: TEMPLATE_QUERY,
				variables: { slug },
				fetchPolicy: 'no-cache',
			})
			.then(({ data }) => {
				const template = Object.entries(data).find(([key, template]) => template !== null)
				return template || Promise.reject(new Error('Preview not found'))
			})
	}, [])

	//First we need to look for all posible templates and create the page context
	const pageContext = usePromise(() => {
		if (templateQuery[STATUS] === RESOLVED) {
			const [key, value] = templateQuery[RESULT]
			const pageContext = pageContextFactory[key](value, key)
			return pageContext.then !== undefined ? pageContext : Promise.resolve(pageContext)
		}
	}, [templateQuery[STATUS], templateQuery[RESULT]])

	const component = usePromise(() => {
		if (pageContext[STATUS] === RESOLVED) {
			const component = pageContext[RESULT].component.replace('/src/', '')
			return importFunction(component)
		}
	}, [pageContext[STATUS], pageContext[RESULT]])

	//Now we can look for the data needed for the template
	const data = usePromise(() => {
		if (pageContext[STATUS] === RESOLVED && component[STATUS] === RESOLVED) {
			const queriesTemplate = queries[pageContext[RESULT].component]
			return Promise.all(
				Object.entries(queriesTemplate).map(([key, query]) => {
					/**
					 * @type {Array}
					 */
					const varNames = query.definitions.map((definition) => {
						if (definition.kind === 'OperationDefinition') {
							return definition.variableDefinitions.map((variableDefinition) => {
								return variableDefinition.variable.name.value
							})
						}
						return []
					})

					const variables = {}
					varNames.flat().forEach((varName) => (variables[varName] = pageContext[RESULT].context[varName]))

					variables.preview = true

					return clients[key].query({
						query,
						variables,
						fetchPolicy: 'no-cache',
					})
				})
			).then((dataArray = []) => dataArray.reduce((current, next) => ({ ...current, ...next }), {}))
		}
	}, [pageContext[STATUS], component[STATUS], pageContext[RESULT], queries])

	return (
		<PreviewComponent templateQuery={templateQuery} pageContext={pageContext} component={component} data={data} />
	)
}
